import { Controller } from "@hotwired/stimulus"

const BODY_LOCK_CLASS = "modal-locked"

export default class extends Controller {
  static targets = ["overlay", "slide", "counter", "previousButton", "nextButton"]

  connect() {
    this.currentIndex = 0
    this.isOpen = false
    this.isTransitioning = false
    this.transitionDuration = 320
    this.transitionTimeout = null
    this.boundHandleKeydown = this.handleKeydown.bind(this)

    window.addEventListener("keydown", this.boundHandleKeydown)
    this.initializeSlides()
    this.showSlide(this.currentIndex)
    this.updateUi()
  }

  disconnect() {
    window.removeEventListener("keydown", this.boundHandleKeydown)
    this.clearTransitionTimeout()
    this.unlockScroll()
  }

  open(event) {
    event.preventDefault()

    const requestedIndex = parseInt(event.currentTarget.dataset.index, 10)
    this.currentIndex = Number.isNaN(requestedIndex) ? 0 : requestedIndex
    this.isOpen = true

    this.overlayTarget.classList.remove("hidden", "opacity-0", "pointer-events-none")
    this.lockScroll()
    this.showSlide(this.currentIndex)
    this.updateUi()
  }

  close(event) {
    if (event) event.preventDefault()

    this.isOpen = false
    this.overlayTarget.classList.add("opacity-0", "pointer-events-none")
    window.setTimeout(() => {
      if (!this.isOpen) {
        this.overlayTarget.classList.add("hidden")
      }
    }, 200)
    this.unlockScroll()
  }

  backdropClose(event) {
    if (event.target === this.overlayTarget) {
      this.close()
    }
  }

  previous(event) {
    if (event) event.preventDefault()
    this.goToIndex(this.wrapIndex(this.currentIndex - 1), -1)
  }

  next(event) {
    if (event) event.preventDefault()
    this.goToIndex(this.wrapIndex(this.currentIndex + 1), 1)
  }

  handleKeydown(event) {
    if (!this.isOpen) return

    if (event.key === "Escape") {
      this.close()
      return
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault()
      this.previous()
      return
    }

    if (event.key === "ArrowRight") {
      event.preventDefault()
      this.next()
    }
  }

  updateUi() {
    const total = this.slideTargets.length

    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${total == 0 ? 0 : this.currentIndex + 1} / ${total}`
    }

    if (this.hasPreviousButtonTarget) {
      this.previousButtonTarget.disabled = total < 2
    }

    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.disabled = total < 2
    }
  }

  wrapIndex(index) {
    const total = this.slideTargets.length
    if (total === 0) return 0

    return ((index % total) + total) % total
  }

  goToIndex(nextIndex, direction) {
    if (this.isTransitioning || nextIndex === this.currentIndex) return

    const currentSlide = this.slideTargets[this.currentIndex]
    const nextSlide = this.slideTargets[nextIndex]
    if (!currentSlide || !nextSlide) return

    this.isTransitioning = true
    this.clearTransitionTimeout()

    this.prepareSlide(currentSlide, 0, 1, 10)
    this.prepareSlide(nextSlide, direction * 100, 1, 20)

    requestAnimationFrame(() => {
      currentSlide.style.transform = `translateX(${-direction * 100}%)`
      currentSlide.style.opacity = "0.9"
      nextSlide.style.transform = "translateX(0)"
      nextSlide.style.opacity = "1"
    })

    this.currentIndex = nextIndex
    this.updateUi()

    this.transitionTimeout = window.setTimeout(() => {
      this.hideSlide(currentSlide)
      this.prepareSlide(nextSlide, 0, 1, 10)
      this.isTransitioning = false
      this.transitionTimeout = null
    }, this.transitionDuration)
  }

  initializeSlides() {
    this.slideTargets.forEach((slide) => this.hideSlide(slide))
  }

  showSlide(index) {
    this.slideTargets.forEach((slide, slideIndex) => {
      if (slideIndex === index) {
        this.prepareSlide(slide, 0, 1, 10)
      } else {
        this.hideSlide(slide)
      }
    })
  }

  prepareSlide(slide, translatePercent, opacity, zIndex) {
    slide.classList.remove("hidden")
    slide.classList.add("flex")
    slide.setAttribute("aria-hidden", "false")
    slide.style.transform = `translateX(${translatePercent}%)`
    slide.style.opacity = String(opacity)
    slide.style.zIndex = String(zIndex)
  }

  hideSlide(slide) {
    slide.classList.add("hidden")
    slide.classList.remove("flex")
    slide.setAttribute("aria-hidden", "true")
    slide.style.transform = "translateX(0)"
    slide.style.opacity = "0"
    slide.style.zIndex = "0"
  }

  clearTransitionTimeout() {
    if (!this.transitionTimeout) return

    window.clearTimeout(this.transitionTimeout)
    this.transitionTimeout = null
    this.isTransitioning = false
  }

  lockScroll() {
    document.documentElement.classList.add(BODY_LOCK_CLASS)
    document.body.classList.add(BODY_LOCK_CLASS)
  }

  unlockScroll() {
    document.documentElement.classList.remove(BODY_LOCK_CLASS)
    document.body.classList.remove(BODY_LOCK_CLASS)
  }
}
